import React, { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { Upload, X, Crop, ZoomIn, ZoomOut } from "lucide-react";

/**
 * Cropper interativo: upload de imagem + pan/zoom em preview circular.
 * onChange(dataUrl|null) — entrega JPEG quadrado 600x600.
 */
export function PhotoUploader({ value, onChange, size = "md" }) {
  const [editing, setEditing] = useState(false);
  const [src, setSrc] = useState(null);     // image source for crop
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 });
  const containerRef = useRef(null);
  const dragRef = useRef(null);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Arquivo não é imagem");
    if (file.size > 8 * 1024 * 1024) return toast.error("Imagem muito grande (máx 8MB)");
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        setNaturalSize({ w: img.width, h: img.height });
        setSrc(ev.target.result);
        // Initial zoom: fit shortest side to container
        const C = 320; // container box size in preview
        const initial = C / Math.min(img.width, img.height);
        setZoom(initial);
        setOffset({ x: 0, y: 0 });
        setEditing(true);
      };
      img.onerror = () => toast.error("Falha ao ler imagem");
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const editExisting = () => {
    if (!value) return;
    const img = new Image();
    img.onload = () => {
      setNaturalSize({ w: img.width, h: img.height });
      setSrc(value);
      const C = 320;
      const initial = C / Math.min(img.width, img.height);
      setZoom(initial);
      setOffset({ x: 0, y: 0 });
      setEditing(true);
    };
    img.src = value;
  };

  // Drag handling
  useEffect(() => {
    if (!editing) return;
    const move = (e) => {
      if (!dragRef.current) return;
      const point = e.touches ? e.touches[0] : e;
      const dx = point.clientX - dragRef.current.startX;
      const dy = point.clientY - dragRef.current.startY;
      setOffset({ x: dragRef.current.baseX + dx, y: dragRef.current.baseY + dy });
    };
    const up = () => { dragRef.current = null; };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", move);
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
    };
  }, [editing]);

  const startDrag = (e) => {
    const point = e.touches ? e.touches[0] : e;
    dragRef.current = { startX: point.clientX, startY: point.clientY, baseX: offset.x, baseY: offset.y };
  };

  const confirmCrop = () => {
    // Output 600x600 representing what's visible in the 320x320 preview circle.
    const OUT = 600;
    const PREVIEW = 320;
    const scale = OUT / PREVIEW;
    const canvas = document.createElement("canvas");
    canvas.width = OUT; canvas.height = OUT;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#0b1320";
    ctx.fillRect(0, 0, OUT, OUT);

    const img = new Image();
    img.onload = () => {
      // Image is rendered at width naturalSize.w * zoom, centered at preview center + offset.
      const w = naturalSize.w * zoom;
      const h = naturalSize.h * zoom;
      // top-left position of image in preview coords:
      const left = (PREVIEW - w) / 2 + offset.x;
      const top = (PREVIEW - h) / 2 + offset.y;
      ctx.drawImage(img, left * scale, top * scale, w * scale, h * scale);
      const dataUrl = canvas.toDataURL("image/jpeg", 0.88);
      onChange(dataUrl);
      setEditing(false);
      setSrc(null);
    };
    img.src = src;
  };

  const dim = size === "lg" ? "size-36" : size === "sm" ? "size-16" : "size-24";

  return (
    <>
      <div className="flex items-center gap-3">
        <div className={`${dim} rounded-full overflow-hidden bg-muted border-2 border-border flex items-center justify-center`}>
          {value ? (
            <img src={value} alt="" className="w-full h-full object-cover" />
          ) : (
            <Upload className="size-6 text-muted-foreground" />
          )}
        </div>
        <div className="flex flex-col gap-1">
          <label>
            <input type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="photo-file-input" />
            <Button type="button" variant="outline" size="sm" asChild>
              <span className="cursor-pointer">{value ? "Trocar foto" : "Enviar foto"}</span>
            </Button>
          </label>
          {value && (
            <div className="flex gap-1">
              <Button type="button" variant="outline" size="sm" onClick={editExisting} data-testid="edit-photo-button">
                <Crop className="size-3 mr-1" /> Ajustar
              </Button>
              <Button type="button" variant="ghost" size="sm" className="text-destructive" onClick={() => onChange(null)}>
                <X className="size-3 mr-1" /> Remover
              </Button>
            </div>
          )}
        </div>
      </div>

      <Dialog open={editing} onOpenChange={(o) => { if (!o) { setEditing(false); setSrc(null); } }}>
        <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="photo-crop-dialog">
          <DialogHeader>
            <DialogTitle>Ajustar foto</DialogTitle>
            <DialogDescription>Arraste pra reposicionar. Use o zoom para enquadrar.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-center">
            <div
              ref={containerRef}
              className="relative bg-black/60 rounded-full overflow-hidden border-2 border-[hsl(var(--vv-lime))]"
              style={{ width: 320, height: 320, touchAction: "none", cursor: "grab" }}
              onMouseDown={startDrag}
              onTouchStart={startDrag}
              data-testid="photo-crop-area"
            >
              {src && (
                <img
                  src={src} alt=""
                  draggable={false}
                  style={{
                    position: "absolute",
                    width: naturalSize.w * zoom,
                    height: naturalSize.h * zoom,
                    left: (320 - naturalSize.w * zoom) / 2 + offset.x,
                    top: (320 - naturalSize.h * zoom) / 2 + offset.y,
                    userSelect: "none",
                    pointerEvents: "none",
                  }}
                />
              )}
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <ZoomOut className="size-4 text-muted-foreground" />
              <Slider
                min={0.1} max={4} step={0.01}
                value={[zoom]}
                onValueChange={(v) => setZoom(v[0])}
                className="flex-1"
                data-testid="photo-zoom-slider"
              />
              <ZoomIn className="size-4 text-muted-foreground" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => { setEditing(false); setSrc(null); }}>Cancelar</Button>
            <Button type="button" onClick={confirmCrop} data-testid="photo-crop-confirm" className="bg-[hsl(var(--vv-lime))] text-[hsl(218_50%_10%)] font-bold">
              Aplicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
